/* ============================================================
   Fence Works — build-step time estimates (crew build sheet)

   Pure data + tiny helpers. Turns each build step (from
   lib/buildsteps.ts) plus the BOM's per-class counts into an
   estimated install time, so the crew sheet can show "~1h 10m"
   next to a step and an "Est. job time" summary.

   How a step's time is derived (NO buildsteps.ts edit needed):
   - Most steps carry `qk` quantity keys that resolve to a count
     via the BOM class counts (the same mapping the 3D viewer and
     the quantity badge use). For those, time = perUnitMinutes ×
     resolved-count, keyed by the matched quantity class.
       e.g. 44 posts × 8 min = 352 min on "Drive steel posts".
   - Steps with no count (e.g. "Mark property", "Shoot grade")
     use a FLAT per-step minutes keyed by the step title.
   - Anything else → 0 (doesn't move the total).

   Counting note: a few steps share one class. Vinyl rails split
   top/bottom (`qk:["rails"], qd:2`), so each rail step resolves
   to count/2 — two steps sum back to the full rails count, which
   is exactly what we want. Gate work is split across several
   `qk:["gates"]` steps, so the per-unit gate rate lands on EACH
   gate step (≈ rate × gates per step → a few × that per gate).

   These are seed defaults — the contractor tweaks them in
   Settings, stored on the flexible `settings.stepRates` object;
   actuals from time-tracking (wo.timeLogs) can refine them later.
   ============================================================ */

import { stepQtyFor } from "./buildsteps";

/* per-UNIT install minutes by quantity class (rate × resolved count) */
export type StepRates = {
  posts: number; sleeves: number; fittings: number; rails: number;
  boards: number; caps: number; screws: number; footings: number;
  fabric: number; ties: number; gates: number;
};

/* seed defaults — minutes per unit (owner-tunable in Settings) */
export const DEFAULT_STEP_RATES: StepRates = {
  posts: 8, sleeves: 3, fittings: 2.5, rails: 4, boards: 0.8,
  caps: 1, screws: 0.4, footings: 15, fabric: 0.3, ties: 0.4, gates: 15,
};

/* flat minutes for the count-less steps, keyed by step title */
export const flatStepMinutes: Record<string, number> = {
  "Mark property": 30,
  "Shoot grade": 20,
};

/* optional SIZE-SCALING companion for the flat steps — minutes ADDED per 100
   linear feet (bigger properties take longer to stake out / shoot grade). Keyed
   by the flat step's title + "/ft" so they ride on the same rates blob, and
   default to 0 → fully opt-in: with these at 0 (or feet unknown) a flat step is
   exactly today's flat minutes. */
export const PER_FT_STEP_OF: Record<string, string> = {
  "Mark property": "Mark property/ft",
  "Shoot grade": "Shoot grade/ft",
};
export const DEFAULT_PER_FT_STEP_MINUTES: Record<string, number> = {
  "Mark property/ft": 0,
  "Shoot grade/ft": 0,
};

/* labels + order for the editable per-unit grid (Settings). Includes the two
   optional per-100ft companions so the global editor can expose them. */
export const STEP_RATE_LABELS: Record<string, string> = {
  posts: "Posts", sleeves: "Sleeves", fittings: "Fittings", rails: "Rails",
  boards: "Boards / pickets", caps: "Caps", screws: "Screws / fasteners",
  footings: "Footings", fabric: "Fabric / mesh", ties: "Ties", gates: "Gate (per gate-step)",
  "Mark property/ft": "Mark property — per 100 ft",
  "Shoot grade/ft": "Shoot grade — per 100 ft",
};
/* per-UNIT class keys (the typed StepRates table) */
export const STEP_RATE_KEYS = Object.keys(DEFAULT_STEP_RATES) as (keyof StepRates)[];
/* the optional per-100ft companion keys (size scaling for the flat steps) */
export const STEP_PER_FT_KEYS = Object.keys(DEFAULT_PER_FT_STEP_MINUTES);

/* merge stored rates over defaults so a partial/absent settings object is safe.
   Resolution chain (later wins): DEFAULT_STEP_RATES ← globalRates ← productRates.
   - Per-unit class rates land on their typed keys.
   - The flat per-step overrides (keyed by step title, e.g. "Mark property") and
     their optional per-100ft companions ("Mark property/ft", default 0) ride
     along on the same object, so the whole table lives in one blob.
   `productRates` is an OPTIONAL sparse override (only the keys a product changes);
   omit it for global-only behavior — the 1-arg call is byte-for-byte unchanged. */
export function ratesWithDefaults(globalRates: any, productRates?: any): StepRates & Record<string, number> {
  const out: any = { ...DEFAULT_STEP_RATES };
  for (const t of Object.keys(flatStepMinutes)) out[t] = flatStepMinutes[t];
  for (const k of STEP_PER_FT_KEYS) out[k] = DEFAULT_PER_FT_STEP_MINUTES[k];
  const apply = (rates: any) => {
    if (!rates || typeof rates !== "object") return;
    for (const k of STEP_RATE_KEYS) if (isFinite(+rates[k])) out[k] = +rates[k];
    for (const t of Object.keys(flatStepMinutes)) if (isFinite(+rates[t])) out[t] = +rates[t];
    for (const k of STEP_PER_FT_KEYS) if (isFinite(+rates[k])) out[k] = +rates[k];
  };
  apply(globalRates);   // global table over defaults
  apply(productRates);  // sparse per-product overrides on top
  return out;
}

/* the quantity class a step's time is priced from — the first `qk` key that
   resolves against the BOM counts (mirrors qtyLabel/stepQtyFor in the sheet) */
function classFor(step: any, counts: any): string | null {
  if (!step || !step.qk || !counts) return null;
  for (const k of step.qk) if (counts[k] != null) return k;
  return null;
}

/* estimated install minutes for ONE step:
   - rate(matched class) × resolved count (count already ÷ qd), or
   - flat per-step minutes (no count) + optional size scaling, or
   - 0 when neither applies.
   `rates` is optional — falls back to defaults. `feet` is the run's linear feet
   for the OPTIONAL flat-step size scaling: a flat step becomes
   `flat + perFt × (feet/100)`. When feet is unknown/0 OR the per-100ft rate is 0
   (the default), it's identical to the plain flat minutes. */
export function stepMinutes(step: any, counts: any, rates?: any, feet: number = 0): number {
  if (!step) return 0;
  const r = ratesWithDefaults(rates);
  const k = classFor(step, counts);
  if (k) {
    const qty = stepQtyFor(step, counts); // same count the qty badge shows (÷ qd)
    const per = (r as any)[k];
    if (qty != null && isFinite(+per)) return Math.max(0, qty * per);
    return 0;
  }
  // count-less step → flat per-step minutes (settings override, else default)
  const flat = (r as any)[step.title];
  if (!isFinite(+flat)) return 0;
  let min = Math.max(0, +flat);
  // optional size scaling: add the per-100ft companion × (feet / 100)
  const perFtKey = PER_FT_STEP_OF[step.title];
  const perFt = perFtKey ? +(r as any)[perFtKey] : 0;
  const ft = +feet;
  if (perFtKey && isFinite(perFt) && perFt > 0 && isFinite(ft) && ft > 0) min += perFt * (ft / 100);
  return Math.max(0, min);
}

/* sum estimated minutes across a list of steps. `feet` threads the run's linear
   feet through to the flat steps' optional size scaling (default 0 → no scaling). */
export function totalMinutes(steps: any[], counts: any, rates?: any, feet: number = 0): number {
  const r = ratesWithDefaults(rates);
  return (Array.isArray(steps) ? steps : []).reduce((sum, st) => sum + stepMinutes(st, counts, r, feet), 0);
}

/* "1h 20m" / "45m" / "2h" — compact human duration from minutes */
export function fmtDur(min: number): string {
  const m = Math.max(0, Math.round(+min || 0));
  if (!m) return "0m";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r}m`;
  if (!r) return `${h}h`;
  return `${h}h ${r}m`;
}
